import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Download, Loader2, Package2, Plus, Edit2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function PackingMaterialsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [lots, setLots] = useState([]);
  const [movements, setMovements] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('all');
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [itemForm, setItemForm] = useState({ name: '', unit: 'pcs', is_active: true });
  const [receiveForm, setReceiveForm] = useState({ warehouse_id: '', packing_material_item_id: '', qty_total: '', stock_ref_no: '', note: '' });
  const [transferForm, setTransferForm] = useState({ from_warehouse_id: '', to_warehouse_id: '', packing_material_item_id: '', qty_required: '', note: '' });

  const packingWarehouses = useMemo(
    () => warehouses.filter((warehouse) => warehouse.tag === 'PACKING' || warehouse.warehouse_type === 'PACKING' || String(warehouse.name || '').toLowerCase().includes('pack')),
    [warehouses]
  );

  const selectedWarehouseLots = useMemo(
    () => (selectedWarehouseId === 'all' ? lots : lots.filter((lot) => lot.warehouse_id === selectedWarehouseId)),
    [lots, selectedWarehouseId]
  );

  const selectedWarehouseMovements = useMemo(
    () => (selectedWarehouseId === 'all' ? movements : movements.filter((movement) => movement.warehouse_id === selectedWarehouseId)),
    [movements, selectedWarehouseId]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, warehousesRes, lotsRes, movementRes] = await Promise.all([
        adminApi.getPackingMaterialItems(),
        adminApi.getWarehouses(),
        adminApi.getPackingMaterialStockLots(),
        adminApi.getPackingMaterialStockMovements(),
      ]);
      setItems(itemsRes.data || []);
      const warehouseRows = warehousesRes.data || [];
      setWarehouses(warehouseRows);
      setLots(lotsRes.data || []);
      setMovements(movementRes.data || []);
      if (!receiveForm.warehouse_id) {
        const defaultPacking = warehouseRows.find((warehouse) => warehouse.tag === 'PACKING' || warehouse.warehouse_type === 'PACKING') || warehouseRows[0];
        setReceiveForm((prev) => ({ ...prev, warehouse_id: prev.warehouse_id || defaultPacking?.id || '' }));
        setTransferForm((prev) => ({ ...prev, from_warehouse_id: prev.from_warehouse_id || defaultPacking?.id || '', to_warehouse_id: prev.to_warehouse_id || defaultPacking?.id || '' }));
      }
    } catch (error) {
      toast.error('Failed to load packing materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm({ name: '', unit: 'pcs', is_active: true });
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ name: item.name || '', unit: item.unit || 'pcs', is_active: item.is_active !== false });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      toast.error('Enter packing material name');
      return;
    }
    setSaving(true);
    try {
      if (editingItem?.id) {
        await adminApi.updatePackingMaterialItem(editingItem.id, itemForm);
        toast.success('Packing material updated');
      } else {
        await adminApi.createPackingMaterialItem(itemForm);
        toast.success('Packing material created');
      }
      setShowItemModal(false);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save packing material');
    } finally {
      setSaving(false);
    }
  };

  const handleReceive = async () => {
    setSaving(true);
    try {
      await adminApi.receivePackingMaterialStock({
        ...receiveForm,
        qty_total: Number(receiveForm.qty_total || 0),
      });
      toast.success('Packing material stock received');
      setShowReceiveModal(false);
      setReceiveForm((prev) => ({ ...prev, packing_material_item_id: '', qty_total: '', stock_ref_no: '', note: '' }));
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to receive stock');
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    setSaving(true);
    try {
      await adminApi.transferPackingMaterialStock({
        ...transferForm,
        qty_required: Number(transferForm.qty_required || 0),
      });
      toast.success('Packing material stock transferred');
      setShowTransferModal(false);
      setTransferForm((prev) => ({ ...prev, packing_material_item_id: '', qty_required: '', note: '' }));
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to transfer stock');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const dataset = activeTab === 'movements' ? 'packing-material-movements' : 'packing-material-lots';
      const res = await adminApi.downloadAdminDatasetExcel(dataset, {
        warehouse_id: selectedWarehouseId !== 'all' ? selectedWarehouseId : undefined,
      });
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  return (
    <div className="space-y-6" data-testid="packing-materials-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Packing Materials</h1>
          <p className="text-[#A8A8A8] mt-1">Manage packing material masters, stock lots, and consumption movements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#E5E5E5]" onClick={handleDownloadExcel}>
            <Download className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
          <Button variant="outline" className="border-[#E5E5E5]" onClick={() => setShowReceiveModal(true)}>Receive Stock</Button>
          <Button variant="outline" className="border-[#E5E5E5]" onClick={() => setShowTransferModal(true)}>Transfer Stock</Button>
          <Button onClick={openCreateItem} className="bg-[#C97A5E] hover:bg-[#B06850]">
            <Plus className="w-4 h-4 mr-2" />
            Add Material
          </Button>
        </div>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full md:w-[420px] grid-cols-3">
              <TabsTrigger value="items">Items</TabsTrigger>
              <TabsTrigger value="stock">Current Stock</TabsTrigger>
              <TabsTrigger value="movements">Movements</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="w-full md:w-80">
            <Label>Warehouse</Label>
            <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
              <SelectTrigger className="border-[#E5E5E5]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All packing warehouses</SelectItem>
                {packingWarehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" /></div>
          ) : activeTab === 'items' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Unit</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{item.unit}</td>
                      <td className="py-3 px-4">
                        <Badge className={cn('text-xs', item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditItem(item)}><Edit2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : activeTab === 'stock' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Material</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Stock Ref</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Qty</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {selectedWarehouseLots.map((lot) => (
                    <tr key={lot.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420]">{lot.warehouse_name}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{lot.packing_material_item_name}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{lot.stock_ref_no}</td>
                      <td className="py-3 px-4 text-right text-[#2D2420]">{lot.qty_total} {lot.unit}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(lot.received_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Material</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Qty</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {selectedWarehouseMovements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(movement.created_at)}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{movement.warehouse_name}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{movement.packing_material_item_name}</td>
                      <td className="py-3 px-4 text-[#2D2420] capitalize">{String(movement.type || '').replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-right text-[#2D2420]">{movement.qty} {movement.unit}</td>
                      <td className="py-3 px-4 text-sm text-[#5C4033]">{movement.production_batch_ref || movement.stock_ref_no || movement.ref_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && ((activeTab === 'items' && items.length === 0) || (activeTab === 'stock' && selectedWarehouseLots.length === 0) || (activeTab === 'movements' && selectedWarehouseMovements.length === 0)) ? (
            <div className="flex items-center justify-center h-48 text-[#A8A8A8]">
              <Package2 className="w-6 h-6 mr-2" />
              No data available yet
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingItem ? 'Update Packing Material' : 'Add Packing Material'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={itemForm.name} onChange={(e) => setItemForm((prev) => ({ ...prev, name: e.target.value }))} className="border-[#E5E5E5]" /></div>
            <div><Label>Unit</Label><Input value={itemForm.unit} onChange={(e) => setItemForm((prev) => ({ ...prev, unit: e.target.value }))} className="border-[#E5E5E5]" /></div>
            <div className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-3 py-2">
              <Label className="text-sm">Active</Label>
              <Switch checked={itemForm.is_active} onCheckedChange={(value) => setItemForm((prev) => ({ ...prev, is_active: value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSaveItem} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Receive Packing Material Stock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Warehouse</Label><Select value={receiveForm.warehouse_id} onValueChange={(value) => setReceiveForm((prev) => ({ ...prev, warehouse_id: value }))}><SelectTrigger className="border-[#E5E5E5]"><SelectValue /></SelectTrigger><SelectContent>{packingWarehouses.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Material</Label><Select value={receiveForm.packing_material_item_id} onValueChange={(value) => setReceiveForm((prev) => ({ ...prev, packing_material_item_id: value }))}><SelectTrigger className="border-[#E5E5E5]"><SelectValue /></SelectTrigger><SelectContent>{items.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Quantity</Label><Input type="number" value={receiveForm.qty_total} onChange={(e) => setReceiveForm((prev) => ({ ...prev, qty_total: e.target.value }))} className="border-[#E5E5E5]" /></div>
            <div><Label>Stock Ref No</Label><Input value={receiveForm.stock_ref_no} onChange={(e) => setReceiveForm((prev) => ({ ...prev, stock_ref_no: e.target.value }))} className="border-[#E5E5E5]" placeholder="Auto generated if blank" /></div>
            <div><Label>Note</Label><Input value={receiveForm.note} onChange={(e) => setReceiveForm((prev) => ({ ...prev, note: e.target.value }))} className="border-[#E5E5E5]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleReceive} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receive'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Transfer Packing Material Stock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>From Warehouse</Label><Select value={transferForm.from_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, from_warehouse_id: value }))}><SelectTrigger className="border-[#E5E5E5]"><SelectValue /></SelectTrigger><SelectContent>{packingWarehouses.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>To Warehouse</Label><Select value={transferForm.to_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, to_warehouse_id: value }))}><SelectTrigger className="border-[#E5E5E5]"><SelectValue /></SelectTrigger><SelectContent>{warehouses.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Material</Label><Select value={transferForm.packing_material_item_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, packing_material_item_id: value }))}><SelectTrigger className="border-[#E5E5E5]"><SelectValue /></SelectTrigger><SelectContent>{items.filter((item) => item.is_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Quantity</Label><Input type="number" value={transferForm.qty_required} onChange={(e) => setTransferForm((prev) => ({ ...prev, qty_required: e.target.value }))} className="border-[#E5E5E5]" /></div>
            <div><Label>Note</Label><Input value={transferForm.note} onChange={(e) => setTransferForm((prev) => ({ ...prev, note: e.target.value }))} className="border-[#E5E5E5]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
